import { useJobStream } from './useJobStream';
import { STATUS_TONE, isTerminalStatus, resultSummary, statusLabel, stepDetail, stepIcon, stepLabel, topSections } from '../../lib/jobView';

const asText = (v: unknown) => (Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v));

/**
 * One job's live steps (ported from chatbot_front hub/JobPanel.js). The row's status (Postgres) wins when it is final:
 * three backend paths finish a job without an event, and the stream would otherwise claim it is still working.
 * A finished job's events may have aged out of Redis (1 000 per job) — then only the row's status shows.
 */
export function JobPanel({ system, botId, jobId, jobStatus }: { system: string; botId: string; jobId: string; jobStatus: string }) {
  const { events, status, result, error, retries, streamError } = useJobStream(system, botId, jobId);
  const effective = isTerminalStatus(jobStatus) ? jobStatus : status || jobStatus;
  const terminal = isTerminalStatus(effective);

  const order: string[] = [];
  const byStep: Record<string, string> = {};
  const dataByStep: Record<string, unknown> = {};
  let chunks: Record<string, unknown>[] | null = null;
  let sources: Record<string, unknown>[] | null = null;
  for (const e of events) {
    if (e.step && !(e.step in byStep)) order.push(e.step);
    if (e.step) byStep[e.step] = e.type;
    if (e.step && e.data) dataByStep[e.step] = e.data;
    if (e.data && Array.isArray(e.data.chunks)) chunks = e.data.chunks;
    if (e.data && Array.isArray(e.data.sources)) sources = e.data.sources;
  }
  const connecting = !terminal && !streamError && status === 'pending' && order.length === 0;

  return (
    <div className="mt-2 p-3 bg-white border border-gray-200 rounded space-y-2 text-sm">
      <div className="flex items-center gap-2">
        <span className={`text-xs px-1.5 py-0.5 rounded ${STATUS_TONE[effective] ?? 'bg-gray-100 text-gray-700'}`}>{statusLabel(effective)}</span>
        <code className="text-xs text-gray-400">{jobId}</code>
      </div>
      {order.length > 0 && (
        <ul className="space-y-0.5">
          {order.map((step) => {
            const detail = stepDetail(step, dataByStep[step]);
            const sections = step === 'discover' ? topSections((dataByStep[step] as { histogram?: unknown })?.histogram) : [];
            return (
              <li key={step}>
                <span className="inline-block w-5">{stepIcon(byStep[step], terminal)}</span>
                {stepLabel(step)}
                {detail && <span className="text-gray-500 text-xs"> — {detail}</span>}
                {sections.length > 1 && (
                  <div className="ml-5 text-xs text-gray-500">
                    {sections.map((r) => (
                      <span key={r.path} className="mr-3">
                        {r.path} <b>{r.count}</b>
                      </span>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {connecting && <p className="text-xs text-gray-400">Connecting…</p>}
      {streamError && !terminal && <p className="text-xs text-amber-700">{streamError}</p>}
      {result && <p className="text-green-700 font-medium">✓ {resultSummary(result)}</p>}
      {retries.map((r) => (
        <p key={`retry-${r.attempt}`} className="text-xs text-amber-700">
          Attempt {r.attempt} failed and was retried: {r.error}
        </p>
      ))}
      {error && <p className="text-red-600">Error: {error}</p>}
      {sources && sources.length > 0 && (
        <details>
          <summary className="cursor-pointer">Sources ({sources.length})</summary>
          <ul className="text-xs pl-4 list-disc">
            {sources.map((s, i) => (
              <li key={i}>
                {asText(s.url || s.source_id)}
                {s.changed === false ? ' · unchanged' : ''}
                {s.version ? ` · v${asText(s.version)}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
      {chunks && chunks.length > 0 && (
        <details open>
          <summary className="cursor-pointer">Chunks ({chunks.length})</summary>
          <div className="max-h-80 overflow-auto space-y-1 mt-1">
            {chunks.map((c, i) => (
              <div key={i} className="border rounded p-2 text-xs">
                {asText(c.keywords) && (
                  <div>
                    <b>Keywords:</b> {asText(c.keywords)}
                  </div>
                )}
                {asText(c.sample_question) && (
                  <div className="text-gray-600">
                    <b>Sample question:</b> {asText(c.sample_question)}
                  </div>
                )}
                {c.content ? (
                  <div className="mt-1 whitespace-pre-wrap">
                    {String(c.content).slice(0, 300)}
                    {String(c.content).length > 300 ? '…' : ''}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
